import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Layers,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Loader2,
  Copy,
  Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default template values
const DEFAULT_TEMPLATE = {
  name: "",
  template_type: "POLAROID",
  page_width: 18.0,
  page_height: 12.0,
  dpi: 300,
  columns: 6,
  rows: 3,
  frame_width: 3.0,
  frame_height: 4.0,
  spacing: 0.0,
  inner_padding_top: 0.3,
  inner_padding_bottom: 0.7,
  inner_padding_left: 0.3,
  inner_padding_right: 0.3,
  background_color: "#FFFFFF",
  frame_color: "#FFFFFF",
  separator_color: "#333333",
  separator_width: 2,
  crop_mark_length: 5,  // mm (millimeters)
  show_crop_marks: true,
  show_caption: false,
  caption_font: "Poppins",
  caption_font_size: 12,
  caption_font_color: "#000000",
  caption_alignment: "center",
  caption_area_height: 30,
  // Page Footer Settings
  show_page_footer: false,
  page_footer_show_order_id: true,
  page_footer_show_page_number: true,
  page_footer_show_account: false,
  page_footer_custom_text: "",
  page_footer_font_size: 10,
  page_footer_font_color: "#666666",
  is_active: true,
};

// Available fonts for captions
const CAPTION_FONTS = [
  { value: "Poppins", label: "Poppins (Modern)" },
  { value: "Helvetica", label: "Helvetica (Clean)" },
  { value: "Amatic SC", label: "Amatic SC (Handwritten)" },
  { value: "Homemade Apple", label: "Homemade Apple (Script)" },
  { value: "Courier New", label: "Courier New (Typewriter)" },
  { value: "Special Elite", label: "Special Elite (Retro)" },
];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(DEFAULT_TEMPLATE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/print-templates`);
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm(DEFAULT_TEMPLATE);
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({ ...DEFAULT_TEMPLATE, ...template });
    setTemplateDialogOpen(true);
  };

  const handleDuplicateTemplate = (template) => {
    setEditingTemplate(null);
    setTemplateForm({
      ...DEFAULT_TEMPLATE,
      ...template,
      name: `${template.name}_copy`,
      id: undefined,
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name) {
      toast.error("Template name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/print-templates/${editingTemplate.id}`, templateForm);
        toast.success("Template updated successfully");
      } else {
        await axios.post(`${API}/print-templates`, templateForm);
        toast.success("Template created successfully");
      }
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg[0]?.msg || "Failed to save template");
      } else {
        toast.error("Failed to save template");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await axios.delete(`${API}/print-templates/${templateToDelete.id}`);
      toast.success("Template deleted");
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error("Failed to delete template");
    }
  };

  const calculateFramesPerPage = () => {
    return templateForm.columns * templateForm.rows;
  };

  return (
    <div className="flex h-screen overflow-hidden" data-testid="templates-page">
      <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="glass-header px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Layers className="h-6 w-6 text-primary" />
              Print Templates
            </h1>
            <p className="text-sm text-muted-foreground">
              Design and manage templates for polaroid printing
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={fetchTemplates}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={handleCreateTemplate}>
              <Plus className="h-4 w-4 mr-2" />
              New Template
            </Button>
          </div>
        </header>

        {/* Stats */}
        <div className="px-6 py-4 grid grid-cols-3 gap-3 max-w-xl">
          <Card className="shadow-soft">
            <CardContent className="p-4">
              <div className="text-2xl font-bold">{templates.length}</div>
              <div className="text-xs text-muted-foreground">Total Templates</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-emerald-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-emerald-700">
                {templates.filter(t => t.is_active).length}
              </div>
              <div className="text-xs text-muted-foreground">Active</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft bg-gray-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-700">
                {templates.filter(t => !t.is_active).length}
              </div>
              <div className="text-xs text-muted-foreground">Inactive</div>
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <div className="flex-1 px-6 pb-6 overflow-hidden">
          <Card className="h-full flex flex-col shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">All Templates</CardTitle>
              <CardDescription>
                Templates match product detail codes (e.g., "36_3x4_W" = 36 images, 3x4 frame, White)
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : templates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Layers className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground font-medium">No templates yet</p>
                  <p className="text-sm text-muted-foreground/70 mb-4">
                    Create a template to define your print layout
                  </p>
                  <Button onClick={handleCreateTemplate}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Page Size</TableHead>
                        <TableHead>Grid</TableHead>
                        <TableHead>Frame Size</TableHead>
                        <TableHead>Frames/Page</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {templates.map((template) => (
                        <TableRow key={template.id}>
                          <TableCell className="font-medium font-mono">
                            {template.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{template.template_type}</Badge>
                          </TableCell>
                          <TableCell>
                            {template.page_width}" × {template.page_height}"
                          </TableCell>
                          <TableCell>
                            {template.columns} × {template.rows}
                          </TableCell>
                          <TableCell>
                            {template.frame_width}" × {template.frame_height}"
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {template.columns * template.rows}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={template.is_active ? "default" : "secondary"}>
                              {template.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDuplicateTemplate(template)}
                                title="Duplicate"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditTemplate(template)}
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive"
                                onClick={() => {
                                  setTemplateToDelete(template);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
            <DialogDescription>
              Define the layout for your print template. Frames per page: {calculateFramesPerPage()}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label>Template Name *</Label>
                <Input
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="e.g., 36_3x4_W"
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Match with product_details code: [count]_[width]x[height]_[color]
                </p>
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={templateForm.template_type}
                  onValueChange={(v) => setTemplateForm({ ...templateForm, template_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="POLAROID">Polaroid</SelectItem>
                    <SelectItem value="GRID">Grid</SelectItem>
                    <SelectItem value="CUSTOM">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Page Size */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Page Size</Label>
              <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-xs">Width (inches)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={templateForm.page_width}
                    onChange={(e) => setTemplateForm({ ...templateForm, page_width: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Height (inches)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    value={templateForm.page_height}
                    onChange={(e) => setTemplateForm({ ...templateForm, page_height: parseFloat(e.target.value) || 0 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">DPI</Label>
                  <Input
                    type="number"
                    value={templateForm.dpi}
                    onChange={(e) => setTemplateForm({ ...templateForm, dpi: parseInt(e.target.value) || 300 })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Grid Layout */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Grid Layout</Label>
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <Label className="text-xs">Columns</Label>
                  <Input
                    type="number"
                    min="1"
                    value={templateForm.columns}
                    onChange={(e) => setTemplateForm({ ...templateForm, columns: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Rows</Label>
                  <Input
                    type="number"
                    min="1"
                    value={templateForm.rows}
                    onChange={(e) => setTemplateForm({ ...templateForm, rows: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Frame Size */}
            <div>
              <Label className="text-sm font-medium mb-2 block">📐 Frame Size (Single Polaroid)</Label>
              <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <Label className="text-xs text-blue-700">Frame Width (inches)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={templateForm.frame_width}
                    onChange={(e) => setTemplateForm({ ...templateForm, frame_width: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-blue-700">Frame Height (inches)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={templateForm.frame_height}
                    onChange={(e) => setTemplateForm({ ...templateForm, frame_height: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Image Gap/Padding */}
            <div>
              <Label className="text-sm font-medium mb-2 block">📏 Image Gap from Frame Edge (inches)</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Actual Image Size: {(templateForm.frame_width - templateForm.inner_padding_left - templateForm.inner_padding_right).toFixed(2)}" × {(templateForm.frame_height - templateForm.inner_padding_top - templateForm.inner_padding_bottom).toFixed(2)}"
              </p>
              <div className="grid grid-cols-4 gap-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <Label className="text-xs text-amber-700">Gap Top</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={templateForm.inner_padding_top}
                    onChange={(e) => setTemplateForm({ ...templateForm, inner_padding_top: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-700">Gap Bottom</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={templateForm.inner_padding_bottom}
                    onChange={(e) => setTemplateForm({ ...templateForm, inner_padding_bottom: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-700">Gap Left</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={templateForm.inner_padding_left}
                    onChange={(e) => setTemplateForm({ ...templateForm, inner_padding_left: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label className="text-xs text-amber-700">Gap Right</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={templateForm.inner_padding_right}
                    onChange={(e) => setTemplateForm({ ...templateForm, inner_padding_right: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
              
              {/* Visual Frame Preview */}
              <div className="mt-4 flex justify-center">
                <div className="relative" style={{ width: '150px', height: `${150 * (templateForm.frame_height / templateForm.frame_width)}px` }}>
                  {/* Frame */}
                  <div 
                    className="absolute inset-0 rounded-lg border-2 border-dashed border-slate-400"
                    style={{ backgroundColor: templateForm.frame_color }}
                  >
                    {/* Gap Labels */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 text-[10px] text-amber-600 font-bold bg-amber-100 px-1 rounded">
                      {templateForm.inner_padding_top}"
                    </div>
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[10px] text-amber-600 font-bold bg-amber-100 px-1 rounded">
                      {templateForm.inner_padding_bottom}"
                    </div>
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 text-[10px] text-amber-600 font-bold bg-amber-100 px-1 rounded" style={{ writingMode: 'vertical-lr', transform: 'translateY(-50%) rotate(180deg)' }}>
                      {templateForm.inner_padding_left}"
                    </div>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 text-[10px] text-amber-600 font-bold bg-amber-100 px-1 rounded" style={{ writingMode: 'vertical-lr' }}>
                      {templateForm.inner_padding_right}"
                    </div>
                    
                    {/* Image Area */}
                    <div 
                      className="absolute bg-slate-200 border border-slate-300 flex items-center justify-center text-xs text-slate-500"
                      style={{
                        top: `${(templateForm.inner_padding_top / templateForm.frame_height) * 100}%`,
                        bottom: `${(templateForm.inner_padding_bottom / templateForm.frame_height) * 100}%`,
                        left: `${(templateForm.inner_padding_left / templateForm.frame_width) * 100}%`,
                        right: `${(templateForm.inner_padding_right / templateForm.frame_width) * 100}%`,
                      }}
                    >
                      IMAGE
                    </div>
                  </div>
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-slate-500 whitespace-nowrap">
                    Frame: {templateForm.frame_width}" × {templateForm.frame_height}"
                  </div>
                </div>
              </div>
            </div>

            {/* Frame Spacing */}
            <div>
              <Label className="text-sm font-medium mb-2 block">↔️ Spacing Between Frames</Label>
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="max-w-[200px]">
                  <Label className="text-xs text-slate-700">Gap Between Frames (inches)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={templateForm.spacing}
                    onChange={(e) => setTemplateForm({ ...templateForm, spacing: parseFloat(e.target.value) || 0 })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            </div>

            {/* Crop Marks (Trim Marks) */}
            <div>
              <Label className="text-sm font-medium mb-2 block">✂️ Crop Marks (Page Edge Guides)</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Small marks at page corners for cutting alignment
              </p>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-4 mb-4">
                  <Switch
                    checked={templateForm.show_crop_marks}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, show_crop_marks: checked })}
                  />
                  <Label className="text-sm text-purple-700">Show Crop Marks</Label>
                </div>
                {templateForm.show_crop_marks && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-purple-700">Mark Length (mm)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="50"
                        value={templateForm.crop_mark_length}
                        onChange={(e) => setTemplateForm({ ...templateForm, crop_mark_length: parseFloat(e.target.value) || 5 })}
                        className="mt-1 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-purple-700">Line Width (pixels)</Label>
                      <Input
                        type="number"
                        step="1"
                        min="1"
                        max="10"
                        value={templateForm.separator_width}
                        onChange={(e) => setTemplateForm({ ...templateForm, separator_width: parseInt(e.target.value) || 2 })}
                        className="mt-1 bg-white"
                      />
                    </div>
                  </div>
                )}
                
                {/* Visual Preview of Cutting Marks Between Frames */}
                {templateForm.show_crop_marks && (
                  <div className="mt-4">
                    <p className="text-xs text-purple-600 mb-2 text-center">
                      For {templateForm.columns} cols × {templateForm.rows} rows: 
                      TOP={templateForm.columns - 1}, BOTTOM={templateForm.columns - 1}, LEFT={templateForm.rows - 1}, RIGHT={templateForm.rows - 1} marks
                    </p>
                    <div className="flex justify-center">
                      <div className="relative bg-slate-100 border border-slate-300 rounded" style={{ width: '180px', height: '90px' }}>
                        {/* Grid lines to show frames */}
                        <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${Math.min(templateForm.columns, 6)}, 1fr)`, gridTemplateRows: `repeat(${Math.min(templateForm.rows, 3)}, 1fr)` }}>
                          {Array.from({ length: Math.min(templateForm.columns, 6) * Math.min(templateForm.rows, 3) }).map((_, i) => (
                            <div key={i} className="border border-slate-200"></div>
                          ))}
                        </div>
                        {/* TOP cutting marks */}
                        {Array.from({ length: Math.min(templateForm.columns - 1, 5) }).map((_, i) => (
                          <div key={`top-${i}`} className="absolute w-0.5 h-2 bg-red-500" style={{ 
                            left: `${((i + 1) / Math.min(templateForm.columns, 6)) * 100}%`,
                            top: 0,
                            transform: 'translateX(-50%)'
                          }}></div>
                        ))}
                        {/* BOTTOM cutting marks */}
                        {Array.from({ length: Math.min(templateForm.columns - 1, 5) }).map((_, i) => (
                          <div key={`bottom-${i}`} className="absolute w-0.5 h-2 bg-red-500" style={{ 
                            left: `${((i + 1) / Math.min(templateForm.columns, 6)) * 100}%`,
                            bottom: 0,
                            transform: 'translateX(-50%)'
                          }}></div>
                        ))}
                        {/* LEFT cutting marks */}
                        {Array.from({ length: Math.min(templateForm.rows - 1, 2) }).map((_, i) => (
                          <div key={`left-${i}`} className="absolute h-0.5 w-2 bg-red-500" style={{ 
                            top: `${((i + 1) / Math.min(templateForm.rows, 3)) * 100}%`,
                            left: 0,
                            transform: 'translateY(-50%)'
                          }}></div>
                        ))}
                        {/* RIGHT cutting marks */}
                        {Array.from({ length: Math.min(templateForm.rows - 1, 2) }).map((_, i) => (
                          <div key={`right-${i}`} className="absolute h-0.5 w-2 bg-red-500" style={{ 
                            top: `${((i + 1) / Math.min(templateForm.rows, 3)) * 100}%`,
                            right: 0,
                            transform: 'translateY(-50%)'
                          }}></div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Colors */}
            <div>
              <Label className="text-sm font-medium mb-2 block">🎨 Colors</Label>
              <div className="grid grid-cols-3 gap-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div>
                  <Label className="text-xs text-emerald-700">Page Background</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={templateForm.background_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, background_color: e.target.value })}
                      className="w-12 h-9 p-1 cursor-pointer bg-white"
                    />
                    <Input
                      value={templateForm.background_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, background_color: e.target.value })}
                      className="flex-1 font-mono text-sm bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-emerald-700">Frame Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={templateForm.frame_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, frame_color: e.target.value })}
                      className="w-12 h-9 p-1 cursor-pointer bg-white"
                    />
                    <Input
                      value={templateForm.frame_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, frame_color: e.target.value })}
                      className="flex-1 font-mono text-sm bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-emerald-700">Crop Mark Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={templateForm.separator_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, separator_color: e.target.value })}
                      className="w-12 h-9 p-1 cursor-pointer bg-white"
                    />
                    <Input
                      value={templateForm.separator_color}
                      onChange={(e) => setTemplateForm({ ...templateForm, separator_color: e.target.value })}
                      className="flex-1 font-mono text-sm bg-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Caption Settings */}
            <div>
              <Label className="text-sm font-medium mb-2 block">✍️ Caption Settings</Label>
              <div className="p-4 bg-pink-50 rounded-lg border border-pink-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-pink-700">Enable Captions</Label>
                    <p className="text-xs text-pink-600">Show text captions under each photo</p>
                  </div>
                  <Switch
                    checked={templateForm.show_caption}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, show_caption: checked })}
                  />
                </div>
                
                {templateForm.show_caption && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-pink-700">Font Family</Label>
                        <Select
                          value={templateForm.caption_font}
                          onValueChange={(value) => setTemplateForm({ ...templateForm, caption_font: value })}
                        >
                          <SelectTrigger className="mt-1 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CAPTION_FONTS.map((font) => (
                              <SelectItem key={font.value} value={font.value}>
                                {font.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-pink-700">Font Size (px)</Label>
                        <Input
                          type="number"
                          min={8}
                          max={24}
                          value={templateForm.caption_font_size}
                          onChange={(e) => setTemplateForm({ ...templateForm, caption_font_size: parseInt(e.target.value) || 12 })}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-pink-700">Font Color</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="color"
                            value={templateForm.caption_font_color}
                            onChange={(e) => setTemplateForm({ ...templateForm, caption_font_color: e.target.value })}
                            className="w-12 h-9 p-1 cursor-pointer bg-white"
                          />
                          <Input
                            value={templateForm.caption_font_color}
                            onChange={(e) => setTemplateForm({ ...templateForm, caption_font_color: e.target.value })}
                            className="flex-1 font-mono text-sm bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-pink-700">Text Alignment</Label>
                        <Select
                          value={templateForm.caption_alignment}
                          onValueChange={(value) => setTemplateForm({ ...templateForm, caption_alignment: value })}
                        >
                          <SelectTrigger className="mt-1 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Left</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="right">Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-pink-700">Caption Height (px)</Label>
                        <Input
                          type="number"
                          min={20}
                          max={60}
                          value={templateForm.caption_area_height}
                          onChange={(e) => setTemplateForm({ ...templateForm, caption_area_height: parseInt(e.target.value) || 30 })}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                    
                    {/* Caption Preview */}
                    <div className="mt-4 p-4 bg-white rounded-lg border">
                      <Label className="text-xs text-pink-700 mb-2 block">Preview</Label>
                      <div className="flex items-center justify-center">
                        <div 
                          className="border rounded-lg overflow-hidden shadow-sm"
                          style={{ width: '120px' }}
                        >
                          {/* Mini polaroid frame */}
                          <div 
                            className="p-2 pb-0"
                            style={{ backgroundColor: templateForm.frame_color }}
                          >
                            <div 
                              className="aspect-[3/4] bg-gradient-to-br from-gray-200 to-gray-300 rounded-sm flex items-center justify-center"
                            >
                              <span className="text-xs text-gray-500">Photo</span>
                            </div>
                          </div>
                          {/* Caption area */}
                          <div 
                            className="px-2 py-1 flex items-center"
                            style={{ 
                              backgroundColor: templateForm.frame_color,
                              minHeight: `${Math.max(20, templateForm.caption_area_height / 2)}px`,
                              justifyContent: templateForm.caption_alignment === 'center' ? 'center' : 
                                             templateForm.caption_alignment === 'right' ? 'flex-end' : 'flex-start'
                            }}
                          >
                            <span 
                              style={{ 
                                fontFamily: templateForm.caption_font,
                                fontSize: `${Math.max(8, templateForm.caption_font_size / 1.5)}px`,
                                color: templateForm.caption_font_color
                              }}
                            >
                              Sample Caption 📸
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Page Footer Settings */}
            <div>
              <Label className="text-sm font-medium mb-2 block">📄 Page Footer (PDF)</Label>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm text-blue-700">Enable Page Footer</Label>
                    <p className="text-xs text-blue-600">Show text at bottom-left of each PDF page</p>
                  </div>
                  <Switch
                    checked={templateForm.show_page_footer}
                    onCheckedChange={(checked) => setTemplateForm({ ...templateForm, show_page_footer: checked })}
                  />
                </div>

                {templateForm.show_page_footer && (
                  <>
                    {/* Fields to show */}
                    <div className="space-y-2">
                      <Label className="text-xs text-blue-700">Fields to Display</Label>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={templateForm.page_footer_show_order_id}
                            onChange={(e) => setTemplateForm({ ...templateForm, page_footer_show_order_id: e.target.checked })}
                            className="rounded"
                          />
                          Order ID
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={templateForm.page_footer_show_page_number}
                            onChange={(e) => setTemplateForm({ ...templateForm, page_footer_show_page_number: e.target.checked })}
                            className="rounded"
                          />
                          Page Number
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={templateForm.page_footer_show_account}
                            onChange={(e) => setTemplateForm({ ...templateForm, page_footer_show_account: e.target.checked })}
                            className="rounded"
                          />
                          Account Name
                        </label>
                      </div>
                    </div>

                    {/* Custom text */}
                    <div>
                      <Label className="text-xs text-blue-700">Custom Text (optional)</Label>
                      <Input
                        placeholder="e.g., ThunderBuddy Prints"
                        value={templateForm.page_footer_custom_text}
                        onChange={(e) => setTemplateForm({ ...templateForm, page_footer_custom_text: e.target.value })}
                        className="mt-1 bg-white"
                      />
                    </div>

                    {/* Font settings */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-blue-700">Font Size (pt)</Label>
                        <Input
                          type="number"
                          min={6}
                          max={18}
                          value={templateForm.page_footer_font_size}
                          onChange={(e) => setTemplateForm({ ...templateForm, page_footer_font_size: parseInt(e.target.value) || 10 })}
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-blue-700">Font Color</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="color"
                            value={templateForm.page_footer_font_color}
                            onChange={(e) => setTemplateForm({ ...templateForm, page_footer_font_color: e.target.value })}
                            className="w-12 h-9 p-1 cursor-pointer bg-white"
                          />
                          <Input
                            value={templateForm.page_footer_font_color}
                            onChange={(e) => setTemplateForm({ ...templateForm, page_footer_font_color: e.target.value })}
                            className="flex-1 font-mono text-sm bg-white"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-3 bg-white rounded border">
                      <Label className="text-xs text-blue-700 mb-2 block">Preview</Label>
                      <div className="text-sm" style={{ color: templateForm.page_footer_font_color, fontSize: `${templateForm.page_footer_font_size}px` }}>
                        {[
                          templateForm.page_footer_show_order_id ? "ORDER123456" : "",
                          templateForm.page_footer_show_page_number ? "Page 1/3" : "",
                          templateForm.page_footer_show_account ? (process.env.REACT_APP_ACCOUNT_NAME || "TB") : "",
                          templateForm.page_footer_custom_text || ""
                        ].filter(Boolean).join(" | ") || "No fields selected"}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
              <div>
                <Label>Active Status</Label>
                <p className="text-xs text-muted-foreground">Active templates are used for design generation</p>
              </div>
              <Switch
                checked={templateForm.is_active}
                onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTemplate ? "Update Template" : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTemplate}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
